/**
 * BreadcrumbBar — Element Hierarchy Navigator
 *
 * Shows the element hierarchy when an element is selected in the visual editor.
 * Users can click breadcrumb segments to select parent elements, or click X to deselect.
 *
 * Example: hero_section > div > h1
 */

import React, { useMemo, useState } from 'react';
import { ChevronRight, X } from 'lucide-react';

// ============================================================================
// UTILITY CLASSES TO SKIP IN LABELS
// ============================================================================

/**
 * Tailwind utility classes that don't provide meaningful context for labeling.
 * We skip these when looking for a "meaningful" class name to show in the breadcrumb.
 */
const SKIP_CLASSES = new Set([
  'flex', 'inline-flex', 'block', 'inline-block', 'inline', 'grid', 'inline-grid',
  'hidden', 'contents', 'flow-root',
  'items-center', 'items-start', 'items-end', 'items-stretch', 'items-baseline',
  'justify-center', 'justify-start', 'justify-end', 'justify-between', 'justify-around', 'justify-evenly',
  'flex-row', 'flex-col', 'flex-wrap', 'flex-nowrap', 'flex-1', 'flex-auto', 'flex-none',
  'relative', 'absolute', 'fixed', 'sticky', 'static',
  'overflow-hidden', 'overflow-auto', 'overflow-scroll', 'overflow-visible',
  'w-full', 'h-full', 'w-auto', 'h-auto', 'min-w-0', 'min-h-0',
  'mx-auto', 'text-center', 'text-left', 'text-right',
  'transition-all', 'transition', 'duration-300', 'duration-200',
  'cursor-pointer', 'select-none', 'pointer-events-none',
  'group', 'peer', 'space-y-0', 'space-x-0',
]);

/**
 * Check if a class is a "utility" class we should skip when building labels.
 * Skips responsive prefixes (sm:, md:, lg:, xl:, 2xl:), padding, margin,
 * gap, border-width, text-size, font-weight, tracking, leading, etc.
 */
const isUtilityClass = (cls) => {
  if (SKIP_CLASSES.has(cls)) return true;
  // Responsive prefixes
  if (/^(sm|md|lg|xl|2xl|max-sm|max-md|max-lg|max-xl|max-2xl):/.test(cls)) return true;
  // Spacing: p-, m-, gap-, space-
  if (/^-?[pm][xytblr]?-/.test(cls)) return true;
  if (/^gap-/.test(cls)) return true;
  if (/^space-[xy]-/.test(cls)) return true;
  // Sizing: w-, h-, min-w-, max-w-, min-h-, max-h-
  if (/^(min-|max-)?(w|h)-/.test(cls)) return true;
  // Text sizing: text-xs, text-sm, text-base, text-lg, text-xl, text-2xl, etc.
  if (/^text-(xs|sm|base|lg|xl|\d*xl|\[)/.test(cls)) return true;
  // Font weight
  if (/^font-(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)$/.test(cls)) return true;
  // Tracking, leading
  if (/^(tracking-|leading-)/.test(cls)) return true;
  // Border width
  if (/^border(-[tblrxy])?(-\d|$)/.test(cls)) return true;
  // Rounded
  if (/^rounded/.test(cls)) return true;
  // Opacity
  if (/^opacity-/.test(cls)) return true;
  // Z-index
  if (/^z-/.test(cls)) return true;
  // Object fit/position
  if (/^object-/.test(cls)) return true;
  // Grid columns/rows/spans
  if (/^(grid-cols-|grid-rows-|col-span-|row-span-)/.test(cls)) return true;
  // Order
  if (/^order-/.test(cls)) return true;
  // Inset, top, right, bottom, left
  if (/^(inset|top|right|bottom|left)-/.test(cls)) return true;
  // Aspect ratio
  if (/^aspect-/.test(cls)) return true;
  // Arbitrary values with brackets
  if (/^\[.*\]$/.test(cls)) return true;
  return false;
};

/**
 * Extract the first meaningful CSS class name from a className string.
 * Returns null if no meaningful class is found.
 */
const getMeaningfulClass = (className) => {
  if (!className) return null;
  const classes = className.split(/\s+/).filter(Boolean);
  for (const cls of classes) {
    if (!isUtilityClass(cls)) {
      // Clean up for display: remove color/bg prefixes for brevity
      return cls;
    }
  }
  return null;
};

// ============================================================================
// NODE LABEL HELPERS
// ============================================================================

/**
 * Get a human-readable label from a JSON node.
 * For blocks: use block.id (e.g., "hero_section", "categories").
 * For structure/children: use element type (div, h1, p, img, etc.).
 */
function getNodeLabel(node, isBlock) {
  if (isBlock && node.id) return node.id.replace(/_/g, ' ');
  const type = node.type || node.structure?.type || 'div';
  return type;
}

/**
 * Get a class hint to append to the label, if a meaningful class exists.
 * Returns a shortened string like ".hero-content" or null.
 */
function getClassHint(node) {
  const className = node.className || node.structure?.className || '';
  const meaningful = getMeaningfulClass(className);
  if (!meaningful) return null;
  // Truncate very long class names
  const display = meaningful.length > 24 ? meaningful.slice(0, 22) + '..' : meaningful;
  return '.' + display;
}

// ============================================================================
// PATH PARSING — Build hierarchy segments from a nodePath string
// ============================================================================

/**
 * Parse a nodePath like "blocks.2.structure.children.1.children.0"
 * and resolve each meaningful segment from the templateJSON.
 *
 * Returns an array of { label, classHint, path } objects, one per
 * meaningful hierarchy level.
 */
function buildBreadcrumbs(nodePath, templateJSON) {
  if (!nodePath || !templateJSON) return [];

  const parts = nodePath.split('.');
  const segments = [];
  let current = templateJSON;
  let runningPath = '';

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    runningPath = runningPath ? `${runningPath}.${part}` : part;

    // Navigate into the current node
    if (current == null) break;

    if (Array.isArray(current)) {
      const idx = parseInt(part, 10);
      if (isNaN(idx) || idx < 0 || idx >= current.length) break;
      current = current[idx];
    } else if (typeof current === 'object') {
      current = current[part];
    } else {
      break;
    }

    if (current == null) break;

    // Determine if this is a meaningful segment worth showing
    // Skip intermediate keys like "structure", "children", "attributes"
    const isIntermediateKey = ['structure', 'children', 'attributes', 'events', 'style'].includes(part);

    if (isIntermediateKey) continue;

    // Check if this is a block-level entry (e.g., blocks.2 -> templateJSON.blocks[2])
    const isBlock = parts[i - 1] === 'blocks' || (i === 1 && parts[0] === 'blocks');

    if (typeof current === 'object' && current !== null) {
      const label = getNodeLabel(current, isBlock);
      const classHint = getClassHint(current);
      segments.push({
        label,
        classHint,
        path: runningPath,
        isBlock,
      });
    }
  }

  return segments;
}

// ============================================================================
// BREADCRUMB SEGMENT COMPONENT
// ============================================================================

function BreadcrumbSegment({ label, classHint, isLast, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '1px',
        background: 'none',
        border: 'none',
        padding: '2px 4px',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        lineHeight: '16px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: isLast ? 600 : 400,
        color: isLast
          ? '#1e293b'
          : hovered
            ? '#6366f1'
            : '#64748b',
        backgroundColor: hovered && !isLast ? 'rgba(99, 102, 241, 0.06)' : 'transparent',
        transition: 'all 0.15s ease',
        whiteSpace: 'nowrap',
        maxWidth: '160px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      <span>{label}</span>
      {classHint && (
        <span style={{
          color: isLast ? '#64748b' : '#94a3b8',
          fontWeight: 400,
          fontSize: '11px',
          opacity: 0.8,
        }}>
          {classHint}
        </span>
      )}
    </button>
  );
}

// ============================================================================
// CLOSE BUTTON
// ============================================================================

function CloseButton({ onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title="Deselect element"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '20px',
        height: '20px',
        background: hovered ? 'rgba(239, 68, 68, 0.08)' : 'transparent',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        padding: 0,
        color: hovered ? '#ef4444' : '#9ca3af',
        transition: 'all 0.15s ease',
        flexShrink: 0,
      }}
    >
      <X size={13} strokeWidth={2} />
    </button>
  );
}

// ============================================================================
// SEPARATOR
// ============================================================================

function Separator() {
  return (
    <span style={{
      color: '#d1d5db',
      fontSize: '11px',
      lineHeight: '16px',
      userSelect: 'none',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
    }}>
      <ChevronRight size={12} strokeWidth={1.5} />
    </span>
  );
}

// ============================================================================
// BREADCRUMB BAR COMPONENT
// ============================================================================

/**
 * BreadcrumbBar — displays the element hierarchy for the currently selected
 * element in the visual editor.
 *
 * Props:
 *   selectedElement: { nodePath, structure, type } — the selected element info
 *   templateJSON: object — full template JSON (to walk hierarchy)
 *   onSelectAncestor: function(nodePath) — called when user clicks a breadcrumb segment
 *   onDeselect: function() — called when user clicks X to deselect
 */
function BreadcrumbBar({ selectedElement, templateJSON, onSelectAncestor, onDeselect }) {
  // Build breadcrumb segments from nodePath
  const segments = useMemo(() => {
    if (!selectedElement?.nodePath || !templateJSON) return [];
    return buildBreadcrumbs(selectedElement.nodePath, templateJSON);
  }, [selectedElement?.nodePath, templateJSON]);

  // Don't render if there's no selected element or no segments
  if (!selectedElement || segments.length === 0) return null;

  return (
    <div style={{
      height: '30px',
      background: '#fafbfc',
      borderBottom: '1px solid #eceef1',
      display: 'flex',
      alignItems: 'center',
      padding: '0 12px',
      gap: '2px',
      flexShrink: 0,
      overflow: 'hidden',
      animation: 'breadcrumbFadeIn 0.2s ease-out',
    }}>
      {/* Breadcrumb segments with separators */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
      }}>
        {segments.map((segment, index) => {
          const isLast = index === segments.length - 1;
          return (
            <React.Fragment key={segment.path}>
              {index > 0 && <Separator />}
              <BreadcrumbSegment
                label={segment.label}
                classHint={segment.classHint}
                isLast={isLast}
                onClick={() => onSelectAncestor(segment.path)}
              />
            </React.Fragment>
          );
        })}
      </div>

      {/* Close / deselect button */}
      <CloseButton onClick={onDeselect} />

      {/* Fade-in animation */}
      <style>{`
        @keyframes breadcrumbFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default BreadcrumbBar;
